/* eslint-disable react/destructuring-assignment */
import Error from '../_error';
import { GuideEpisodeLayout } from 'features/guides/components/GuideEpisodeLayout/GuideEpisodeLayout.component';
import Head from 'next/head';
import { Content, ContentType, Episode, GuideApi } from 'pleinchamp-api-client';
import React, { createContext } from 'react';
import { useSelector } from 'react-redux';
import { ApiConfiguration, axiosInstance } from '@api/business/api.conf';
import { usePlcApiCall } from '@api/business/api.hook';
import { filterGuides } from '@guides/business/Guides.utils';
import { searchContent } from '@search/business/search.utils';
import { userSelectors } from '@user/business/User.store';
import { ApiStatusCode } from '@utils/api.utils';
import { isServer } from '@utils/server';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString, return404 } from '@utils/url';
import { PlcNextPage, PlcPageContextProps } from 'features/business/app.types';

const NB_LAST_GUIDES = 2;

interface Props {
  slug: string;
  guideEpisode: Episode;
  lastGuides: Content[];
  isPreview: boolean;
}

const guideApi = new GuideApi(ApiConfiguration, undefined, axiosInstance);

function fetchGuideEpisode(slug: Props['slug']) {
  return guideApi.getGuideEpisode(slug).then(r => r.data);
}

function fetchLastGuides(slug?: Props['slug']) {
  return searchContent({
    contentTypes: [ContentType.Guide],
    exclude: slug,
    limit: NB_LAST_GUIDES,
    offset: 0,
  }).then(r => filterGuides(r.contents));
}

export const GuideEpisodeContext = createContext<PlcPageContextProps<Props>>({
  guideEpisode: { isValidating: false },
  isPreview: { isValidating: false },
  lastGuides: { isValidating: false },
  slug: { isValidating: false },
});

const GuideEpisodePage: PlcNextPage<Partial<Props>> = props => {
  const slug = { data: props.slug, isValidating: false };
  const isPro = useSelector(userSelectors.isPro);
  const guideEpisode = usePlcApiCall(fetchGuideEpisode, [props.slug || ''], {
    deduping: !isPro,
    initialData: props.guideEpisode,
  });

  const guideId = guideEpisode.data?.guideInfo.id.toString();
  const lastGuides = usePlcApiCall(fetchLastGuides, [guideId]);

  if (guideEpisode.error?.response?.status === ApiStatusCode.notFound) {
    return <Error errorCode={ApiStatusCode.notFound} />;
  }

  return (
    <GuideEpisodeContext.Provider
      value={{
        guideEpisode,
        isPreview: { data: props.isPreview, isValidating: false },
        lastGuides,
        slug,
      }}
    >
      <GuideEpisodeLayout />
      <Head>
        <meta content="max-snippet:-1, max-image-preview:large, max-video-preview:-1" name="robots" />
      </Head>
    </GuideEpisodeContext.Provider>
  );
};

GuideEpisodePage.getInitialProps = async ctx => {
  const { query } = ctx;
  const slug = castUrlParamToString(query.slug);

  if (!slug) {
    return return404(ctx);
  }

  const pageDefaultState = {
    namespacesRequired: [...defaultNamespaces, 'guides'],
    slug,
  };

  if (!isServer()) {
    return pageDefaultState;
  }

  return fetchGuideEpisode(slug)
    .then(guideEpisode => ({ ...pageDefaultState, guideEpisode }))
    .catch(() => pageDefaultState);
};

export default GuideEpisodePage;
